// Helper mappers convert reports API responses into shapes that the
// admin reports UI consumes. Each mapper is defensive so that empty or
// malformed responses fail gracefully instead of breaking charts.

/* eslint-disable no-magic-numbers */

const DEFAULT_GENDER_COLORS = {
  male: '#3B82F6',
  female: '#EC4899',
  others: '#10B981',
};

const DEFAULT_RETENTION_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#F87171', '#A78BFA'];

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function toPercent(value, fallback = 0) {
  if (value === null || value === undefined) return fallback;
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.round(num * 10) / 10;
}

function monthFromPeriod(period) {
  if (!period) return '';
  try {
    const normalized = String(period).trim();
    const fragments = normalized.split(/[-/]/);
    if (fragments.length < 2) return normalized;
    const year = Number(fragments[0]);
    const monthIndex = Number(fragments[1]) - 1;
    if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) return normalized;
    const date = new Date(year, monthIndex, 1);
    if (Number.isNaN(date.getTime())) return normalized;
    return date.toLocaleString('en-US', { month: 'short' });
  } catch (error) {
    return String(period);
  }
}

function normalizeAgeLabel(label) {
  if (!label) return 'Unknown';
  const replacements = {
    '0-9': '0-10',
    '10-19': '11-20',
  };
  return replacements[label] || label;
}

export function mapPatientDistribution(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};
  const ageDistribution = Array.isArray(payload.ageDistribution) ? payload.ageDistribution : [];
  const genderDistribution = Array.isArray(payload.genderDistribution) ? payload.genderDistribution : [];

  const totalAge = ageDistribution.reduce((sum, entry) => sum + toNumber(entry.count), 0);
  const ageData = ageDistribution.map((entry) => {
    const count = toNumber(entry.count);
    const percentage = totalAge ? Math.round((count / totalAge) * 1000) / 10 : 0;
    return {
      age: normalizeAgeLabel(entry.age_group || entry.label || 'Unknown'),
      count,
      percentage,
    };
  });

  const genderTotal = genderDistribution.reduce((sum, entry) => sum + toNumber(entry.count), 0);
  const genderData = genderDistribution.map((entry) => {
    const name = entry.gender_category || entry.gender || entry.name || 'Unknown';
    const normalized = String(name).toLowerCase();
    const count = toNumber(entry.count);
    const color = DEFAULT_GENDER_COLORS[normalized] || '#9CA3AF';
    const percentage = genderTotal ? Math.round((count / genderTotal) * 1000) / 10 : 0;
    return {
      name,
      value: count,
      percentage,
      color,
    };
  });

  return {
    ageData,
    genderData,
    totals: {
      age: totalAge,
      gender: genderTotal,
    },
  };
}

export function mapAppointmentTypes(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? [];
  const source = Array.isArray(payload) ? payload : [];

  return source.map((entry) => {
    const inPersonTotal = toNumber(entry?.inPerson?.total);
    const inPersonReferred = toNumber(entry?.inPerson?.referred);
    const teleTotal = toNumber(entry?.teleconsults?.total);
    const teleReferred = toNumber(entry?.teleconsults?.referred);

    const safeNonReferred = (total, referred) => Math.max(total - referred, 0);

    return {
      month: entry.month || (entry.period ? monthFromPeriod(entry.period) : ''),
      inPerson: {
        total: inPersonTotal,
        referred: inPersonReferred,
        nonReferred: safeNonReferred(inPersonTotal, inPersonReferred),
      },
      teleconsults: {
        total: teleTotal,
        referred: teleReferred,
        nonReferred: safeNonReferred(teleTotal, teleReferred),
      },
      inPersonChange: entry.inPersonChange || '0.0%',
      teleconsultsChange: entry.teleconsultsChange || '0.0%',
    };
  });
}

export function mapPatientGrowthTrends(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? [];
  const source = Array.isArray(payload) ? payload : [];

  const mapped = source.map((entry) => ({
    period: entry.period || entry.month || '',
    month: monthFromPeriod(entry.period || entry.month),
    patients: toNumber(entry.new_patients),
  }));

  return mapped.map((entry, index) => {
    const previous = mapped[index - 1];
    if (!previous || !previous.patients) {
      return { ...entry, change: '+0.0%' };
    }
    const diff = entry.patients - previous.patients;
    const pct = previous.patients ? (diff / previous.patients) * 100 : 0;
    const rounded = Math.round(pct * 10) / 10;
    const sign = rounded >= 0 ? '+' : '';
    return { ...entry, change: `${sign}${rounded}%` };
  });
}

function getOrdinalSuffix(n) {
  if (!Number.isFinite(n)) return 'th';
  const value = Math.abs(Math.trunc(n));
  if (value % 100 >= 11 && value % 100 <= 13) return 'th';
  switch (value % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export function mapVisitRepetitionReturn(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};
  const list = Array.isArray(payload.visitRepetition) ? payload.visitRepetition : [];
  const total = list.reduce((sum, entry) => sum + toNumber(entry.patient_count), 0);

  const visitRepetitionData = list.map((entry) => {
    const count = toNumber(entry.patient_count);
    const percentage = total ? Math.round((count / total) * 1000) / 10 : 0;
    const visitNumber = toNumber(entry.visit_number);
    return {
      visits: `${visitNumber || 0}${getOrdinalSuffix(visitNumber)} Visit`,
      count,
      percentage,
    };
  });

  const returnRate = payload?.returnRate?.overall != null ? toPercent(payload.returnRate.overall) : null;

  return {
    visitRepetitionData,
    returnRate,
  };
}

export function mapPatientRetention(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};
  const retentionByPeriod = Array.isArray(payload.retentionByPeriod) ? payload.retentionByPeriod : [];
  const overallRetention = Array.isArray(payload.overallRetention) ? payload.overallRetention : [];

  const mappedPeriod = retentionByPeriod.map((entry, index) => {
    const count = toNumber(entry.patient_count ?? entry.count);
    return {
      range: entry.return_period || entry.range || `Period ${index + 1}`,
      percentage: entry.percentage != null ? toPercent(entry.percentage) : count,
      color: entry.color || DEFAULT_RETENTION_COLORS[index % DEFAULT_RETENTION_COLORS.length],
      count,
      patient_count: count,
    };
  });

  const mappedOverall = overallRetention.map((entry, index) => {
    const count = toNumber(entry.patient_count ?? entry.count);
    return {
      status: entry.patient_status || entry.status || `Status ${index + 1}`,
      count,
      patient_count: count,
      percentage: entry.percentage != null ? toPercent(entry.percentage) : 0,
      color: entry.color || DEFAULT_RETENTION_COLORS[index % DEFAULT_RETENTION_COLORS.length],
    };
  });

  return {
    retentionByPeriod: mappedPeriod,
    overallRetention: mappedOverall,
  };
}

export function mapPatientFollowup(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};
  const complianceRaw = Array.isArray(payload.followUpCompliance)
    ? payload.followUpCompliance
    : (Array.isArray(payload.follow_up_compliance) ? payload.follow_up_compliance : []);
  const byPeriodRaw = Array.isArray(payload.followUpByPeriod)
    ? payload.followUpByPeriod
    : (Array.isArray(payload.follow_up_by_period) ? payload.follow_up_by_period : []);

  const compliance = complianceRaw.map((entry, index) => {
    const count = toNumber(entry.count);
    const percentage = entry.percentage != null ? toPercent(entry.percentage) : null;
    return {
      status: entry.follow_up_status || entry.status || `Status ${index + 1}`,
      count,
      percentage,
    };
  });

  const totalComplianceCount = compliance.reduce((sum, entry) => sum + entry.count, 0);
  const chartData = compliance.map((entry) => ({
    name: entry.status,
    value: entry.count,
    percentage: entry.percentage != null
      ? entry.percentage
      : (totalComplianceCount ? Math.round((entry.count / totalComplianceCount) * 1000) / 10 : 0),
  }));

  const byPeriod = byPeriodRaw.map((entry, index) => ({
    range: entry.time_period || entry.range || `Period ${index + 1}`,
    count: toNumber(entry.count),
    percentage: entry.percentage != null ? toPercent(entry.percentage) : null,
  }));

  return {
    compliance,
    byPeriod,
    chartData,
  };
}

export function mapPatientVisitFrequency(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};
  const list = Array.isArray(payload.data)
    ? payload.data
    : (Array.isArray(payload) ? payload : []);

  return list
    .map((entry) => ({
      patientId: entry.patient_id ?? entry.id ?? null,
      name: entry.name || entry.patient_name || 'Unknown',
      visitCount: toNumber(entry.visit_count),
      lastVisit: entry.last_visit || entry.lastVisit || null,
    }))
    .sort((a, b) => b.visitCount - a.visitCount);
}

// Map bills report (revenue + refunds) into UI-friendly shape
// Expected apiResp.data structure described in reportService.js comments.
export function mapBillsReport(apiResp) {
  const payload = apiResp?.data ?? apiResp ?? {};

  const revenueRaw = payload.revenue ?? payload.revenueData ?? payload.revenues ?? {};
  const refundsRaw = payload.refunds ?? payload.refundsData ?? payload.refundsList ?? {};

  // Support two shapes:
  // 1) Object-based: { revenue: { total, by_doctor: [...] }, refunds: { total, incomplete, missed, by_doctor: [...] } }
  // 2) Period-array based: { revenue: [{ month/period, total, paid, unpaid }], refunds: [{ month/period, total, incomplete, missed }] }

  const revenue = {
    total: 0,
    byDoctor: [],
    byPeriod: [],
  };

  if (Array.isArray(revenueRaw)) {
    revenue.byPeriod = revenueRaw.map((r) => ({
      period: r.month || r.period || '',
      total: toNumber(r.total, 0),
      paid: toNumber(r.paid, 0),
      unpaid: toNumber(r.unpaid ?? (r.total != null ? (Number(r.total) - Number(r.paid || 0)) : 0), 0),
    }));
    revenue.total = revenue.byPeriod.reduce((s, x) => s + x.total, 0);
  } else {
    revenue.total = toNumber(revenueRaw.total, 0);
    revenue.byDoctor = Array.isArray(revenueRaw.by_doctor)
      ? revenueRaw.by_doctor.map((d) => ({
          doctor_id: d.doctor_id ?? d.id ?? null,
          name: d.name || d.doctor_name || 'Unknown',
          total: toNumber(d.total, 0),
        }))
      : [];
    // support revenueRaw.by_period as alternate
    if (Array.isArray(revenueRaw.by_period)) {
      revenue.byPeriod = revenueRaw.by_period.map((r) => ({
        period: r.month || r.period || '',
        total: toNumber(r.total, 0),
        paid: toNumber(r.paid, 0),
        unpaid: toNumber(r.unpaid, 0),
      }));
    }
  }

  const refunds = {
    total: 0,
    incomplete: 0,
    missed: 0,
    byDoctor: [],
    byPeriod: [],
  };

  if (Array.isArray(refundsRaw)) {
    refunds.byPeriod = refundsRaw.map((r) => ({
      period: r.month || r.period || '',
      total: toNumber(r.total, 0),
      incomplete: toNumber(r.incomplete, 0),
      missed: toNumber(r.missed, 0),
    }));
    refunds.total = refunds.byPeriod.reduce((s, x) => s + x.total, 0);
  } else {
    refunds.total = toNumber(refundsRaw.total, 0);
    refunds.incomplete = toNumber(refundsRaw.incomplete, 0);
    refunds.missed = toNumber(refundsRaw.missed, 0);
    refunds.byDoctor = Array.isArray(refundsRaw.by_doctor)
      ? refundsRaw.by_doctor.map((d) => ({
          doctor_id: d.doctor_id ?? d.id ?? null,
          name: d.name || d.doctor_name || 'Unknown',
          total: toNumber(d.total, 0),
          incomplete: toNumber(d.incomplete, 0),
          missed: toNumber(d.missed, 0),
        }))
      : [];
    if (Array.isArray(refundsRaw.by_period)) {
      refunds.byPeriod = refundsRaw.by_period.map((r) => ({
        period: r.month || r.period || '',
        total: toNumber(r.total, 0),
        incomplete: toNumber(r.incomplete, 0),
        missed: toNumber(r.missed, 0),
      }));
    }
  }

  return { revenue, refunds };
}

export function buildSummaryCards({
  appointmentTypes = [],
  patientRetention = {},
  followUp = {},
  patientGrowth = [],
  visitRepetition,
  // additional optional inputs to make cards resilient to alternate shapes
  activeUsers = [],
  newUsers = [],
  bills = {},
}) {
  const summary = [];

  const retentionOverall = Array.isArray(patientRetention.overallRetention)
    ? patientRetention.overallRetention
    : [];
  const totalPatientsEntry = retentionOverall.find((entry) => /total\s*patients?/i.test(String(entry.status)));
  const returningEntry = retentionOverall.find((entry) => /return/i.test(String(entry.status)));

  let totalPatients = totalPatientsEntry?.count ?? (retentionOverall.reduce((sum, entry) => sum + (entry.count || 0), 0) || 0);
  // Fallbacks: if retention doesn't provide a total, try other common shapes
  if (!totalPatients) {
    // newUsers endpoint sometimes returns counts in an object
    const newUsersCount = newUsers?.data?.totalNewUsers ?? newUsers.totalNewUsers ?? (typeof newUsers === 'number' ? newUsers : 0);
    // activeUsers may be array or object
    const activeUsersCount = Array.isArray(activeUsers) ? activeUsers.length : (activeUsers?.count ?? activeUsers?.total ?? 0);
    totalPatients = totalPatients || newUsersCount || activeUsersCount || 0;
  }
  const returningPatients = returningEntry?.count ?? 0;
  const returningPercent = returningEntry?.percentage ?? (totalPatients ? Math.round((returningPatients / totalPatients) * 1000) / 10 : null);

  summary.push({
    key: 'totalPatients',
    label: 'Total Patients',
    value: totalPatients,
    helper: returningPercent != null ? `${returningPercent}% returning` : `${returningPatients} returning`,
  });

  const followUpCompliance = Array.isArray(followUp.compliance) ? followUp.compliance : [];
  const noFollowUp = followUpCompliance
    .filter((entry) => /no\s*follow/i.test(String(entry.status)))
    .reduce((sum, entry) => sum + (entry.count || 0), 0);
  const totalFollowUp = followUpCompliance.reduce((sum, entry) => sum + (entry.count || 0), 0);
  const followUpCompleted = Math.max(totalFollowUp - noFollowUp, 0);
  const followUpCompletionPct = totalFollowUp ? Math.round((followUpCompleted / totalFollowUp) * 1000) / 10 : null;

  summary.push({
    key: 'followUps',
    label: 'Follow-ups Completed',
    value: followUpCompleted,
    helper: followUpCompletionPct != null ? `${followUpCompletionPct}% completion` : `${noFollowUp} pending`,
  });

  const latestGrowth = patientGrowth.length ? patientGrowth[patientGrowth.length - 1] : null;
  summary.push({
    key: 'newPatients',
    label: 'New Patients (Latest Period)',
    value: latestGrowth?.patients ?? 0,
    helper: latestGrowth?.change || '+0.0%',
  });

  const appointmentLatest = appointmentTypes.length ? appointmentTypes[appointmentTypes.length - 1] : null;
  const latestAppointmentTotal = appointmentLatest
    ? toNumber(appointmentLatest?.inPerson?.total) + toNumber(appointmentLatest?.teleconsults?.total)
    : 0;
  summary.push({
    key: 'appointments',
    label: 'Appointments (Latest Period)',
    value: latestAppointmentTotal,
    helper: `${appointmentLatest?.inPersonChange || '0.0%'} in-clinic / ${appointmentLatest?.teleconsultsChange || '0.0%'} tele` ,
  });

  // If bills data present, expose revenue/refunds as cards (helpful as top-level metrics)
  const revenueTotal = bills?.revenue?.total ?? bills?.revenueTotal ?? bills?.revenue?.sum ?? 0;
  if (revenueTotal) {
    summary.push({
      key: 'revenue',
      label: 'Revenue (Range)',
      value: revenueTotal,
      helper: 'Total revenue in selected range',
    });
  }

  const refundsTotal = bills?.refunds?.total ?? bills?.refundsTotal ?? 0;
  if (refundsTotal) {
    summary.push({
      key: 'refunds',
      label: 'Refunds (Range)',
      value: refundsTotal,
      helper: 'Total refunds in selected range',
    });
  }

  const visitReturnRate = visitRepetition?.returnRate;
  if (visitReturnRate != null) {
    summary.push({
      key: 'returnRate',
      label: 'Overall Return Rate',
      value: visitReturnRate,
      suffix: '%',
      helper: 'Based on visit repetition',
    });
  }

  return summary;
}




const reportMappers = {
  mapPatientDistribution,
  mapAppointmentTypes,
  mapPatientGrowthTrends,
  mapVisitRepetitionReturn,
  mapPatientRetention,
  mapPatientFollowup,
  mapPatientVisitFrequency,
  mapBillsReport,
  buildSummaryCards,
};

export default reportMappers;
